import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import ToolPrepPopup, {
  ToolPrepCloseContext,
} from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepDownloadFlowModals from "../../../shared/Popup/ToolPrepDownloadFlowModals";
import ToolPrepFlatResultStage from "../../../shared/Popup/ToolPrepFlatResultStage";
import ResizePreview from "./ResizePreview";
import ImagePickerModal from "../../../../../components/popups/imagePicker/ImagePickerModal";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import { parsePresetPixelSize } from "../../../../../utils/getTransform";
import { useToolPrepImageSource } from "../../../hooks/useToolPrepImageSource";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import { useToolPrepBakedApplyFlow } from "../../../hooks/useToolPrepBakedApplyFlow";
import { ensureBackgroundRemoved } from "../../../../../utils/ensureBackgroundRemoved";
import { exportResizePrepImageBlob } from "../../../lib/exportToolPrepEffectToPngBlob";
import { hasActiveResizeSettings } from "../../../lib/toolPrepEffectActiveSettings";
import {
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
  TOOL_PREP_RESULT_FOOTER_HINT,
} from "../../../../../utils/editorEntrySession";
import ResizeModeCards from "../Editor/ResizeModeCards";
import ResizeControlMenu, {
  ResizePresetSearch,
} from "../Editor/ResizeControlMenu";
import ResizeCustomSizeFlyout from "../Editor/ResizeCustomSizeFlyout";
import {
  addRecentResizePreset,
  buildCustomResizePreset,
  isCustomResizePreset,
} from "../Editor/customResizePreset";
import "./ResizeToolPrep.css";

const RESIZE_THANK_YOU_COPY = {
  titleDownloading: "Downloading…",
  titleComplete: "Download complete",
  leadDownloading: "Please wait — we’re saving your file to this device.",
  leadComplete: "Your file is saved on this computer or phone. You can find it in your Downloads folder.",
  previewAlt: "Resized image download",
  primaryActionLabel: "Add new image",
};

/**
 * Resize prep flow for Editing Tools → Resize.
 * Reuses editor ResizeControlMenu styles/list; adds Remove BG + Fit/Fill chrome.
 */
const ResizeToolPrep = ({
  originalImageUrl = null,
  imageFile = null,
  removeBgDefault = false,
  removeBgRequired = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
  onDone = () => {},
  onCancel = () => {},
}) => {
  const { getToken } = useAuth();
  const openedFromEditor = useContext(ToolPrepCloseContext) === "editor";
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [resizeMode, setResizeMode] = useState("fit");
  const [removeBg, setRemoveBg] = useState(Boolean(removeBgDefault || removeBgRequired));
  const [cutoutUrl, setCutoutUrl] = useState(cachedCutoutUrl || null);
  const [creationId, setCreationId] = useState(null);
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [doneBusy, setDoneBusy] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutAnchor, setFlyoutAnchor] = useState(null);
  const [catalogCustomPreset, setCatalogCustomPreset] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const bgRequestRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const leftPanelRef = useRef(null);
  const resetAppliedRef = useRef(() => {});

  const {
    sourceImageUrl,
    resolvedFile,
    clearSource,
    openUploadPicker,
    imagePickerProps,
    uploadProgress,
    sourceFileName,
    sourceFileSizeLabel,
  } = useToolPrepImageSource({
    originalImageUrl,
    imageFile,
    busy: bgLoading || doneBusy,
    onSourceChange: () => {
      setCutoutUrl(null);
      setCreationId(null);
      if (!removeBgRequired) setRemoveBg(Boolean(removeBgDefault));
      setBgError(null);
      resetAppliedRef.current();
      setSelectedPreset(null);
      setResizeMode("fit");
      setFlyoutOpen(false);
      setFlyoutAnchor(null);
    },
  });

  const {
    applied: resizeApplied,
    busy: resizeBusy,
    resultUrl: resizedPreviewUrl,
    applyError: resizeApplyError,
    setApplyError: setResizeApplyError,
    apply: applyResize,
    resetApplied,
    downloadBusy,
    handleDownload,
    downloadFlow,
  } = useToolPrepBakedApplyFlow({
    sourceFileName,
    fallbackDownloadName: "resized.png",
    toolKey: "resize",
    shareTitle: "Resize tool",
    shareText: "I resized my photo with LazyStudio's Resize tool!",
    thankYouCopy: RESIZE_THANK_YOU_COPY,
  });

  resetAppliedRef.current = resetApplied;

  useEffect(() => {
    if (cachedCutoutUrl) setCutoutUrl(cachedCutoutUrl);
  }, [cachedCutoutUrl]);

  useSyncScrollbarWidth(scrollBodyRef, [
    selectedPreset,
    searchTerm,
    sourceImageUrl,
    resizeApplied,
  ]);

  const runBackgroundRemoval = useCallback(async () => {
    if (cutoutUrl?.startsWith("blob:") || cutoutUrl?.startsWith("data:")) {
      return { cutoutUrl, creationId };
    }

    const requestId = Symbol("bg");
    bgRequestRef.current = requestId;
    setBgLoading(true);
    setBgError(null);

    try {
      const token = await getToken();
      const { cutoutUrl: nextUrl, creationId: cid } =
        await ensureBackgroundRemoved({
          token,
          imageFile: resolvedFile,
          imageUrl: sourceImageUrl,
          cachedCutoutUrl: cutoutUrl || cachedCutoutUrl,
        });
      if (bgRequestRef.current !== requestId) return null;
      setCutoutUrl(nextUrl);
      if (cid != null) setCreationId(cid);
      onCutoutReady?.(nextUrl);
      return { cutoutUrl: nextUrl, creationId: cid ?? creationId };
    } catch (err) {
      if (bgRequestRef.current === requestId) {
        setBgError(err?.message || "Failed to remove background");
      }
      throw err;
    } finally {
      if (bgRequestRef.current === requestId) {
        setBgLoading(false);
      }
    }
  }, [
    cachedCutoutUrl,
    creationId,
    cutoutUrl,
    getToken,
    onCutoutReady,
    resolvedFile,
    sourceImageUrl,
  ]);

  const handleRemoveBgToggle = async () => {
    if (removeBgRequired || !sourceImageUrl) return;
    const next = !removeBg;
    setRemoveBg(next);
    if (!next) {
      setBgError(null);
      return;
    }
    try {
      await runBackgroundRemoval();
    } catch {
      setRemoveBg(false);
    }
  };

  const previewImageUrl = removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;

  useEffect(() => {
    if (!previewImageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setNaturalSize({
          width: img.naturalWidth || 0,
          height: img.naturalHeight || 0,
        });
      }
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = previewImageUrl;
    return () => {
      cancelled = true;
    };
  }, [previewImageUrl]);

  const closeFlyout = useCallback(() => {
    setFlyoutOpen(false);
    setFlyoutAnchor(null);
  }, []);

  const handlePresetSelect = useCallback(
    (preset) => {
      if (resizeApplied) return;
      if (!parsePresetPixelSize(preset)) return;
      setFlyoutOpen(false);
      setFlyoutAnchor(null);
      resetApplied();
      setResizeApplyError(null);
      setSelectedPreset(preset);
      setResizeMode("fit");
    },
    [resizeApplied, resetApplied, setResizeApplyError]
  );

  const handleCustomPresetActivate = useCallback(
    ({ preset, anchorEl }) => {
      if (resizeApplied) return;
      setCatalogCustomPreset(preset || null);
      setFlyoutAnchor(anchorEl || null);
      setFlyoutOpen(true);
    },
    [resizeApplied]
  );

  const handleCustomContinue = useCallback(
    ({ width, height }) => {
      if (resizeApplied) return;
      const preset = buildCustomResizePreset(
        width,
        height,
        catalogCustomPreset
      );
      if (!preset) return;
      addRecentResizePreset(preset);
      handlePresetSelect(preset);
      closeFlyout();
    },
    [catalogCustomPreset, closeFlyout, handlePresetSelect, resizeApplied]
  );

  const handleResizeModeChange = (mode) => {
    if (resizeApplied) return;
    resetApplied();
    setResizeApplyError(null);
    setResizeMode(mode);
  };

  const buildExport = useCallback(() => {
    const url = previewImageUrl;
    const preset = selectedPreset;
    const mode = resizeMode;
    return () => exportResizePrepImageBlob(url, { preset, resizeMode: mode });
  }, [previewImageUrl, resizeMode, selectedPreset]);

  const handleApplyResize = async () => {
    if (!previewImageUrl || resizeBusy || !hasActiveResizeSettings(selectedPreset)) {
      return;
    }
    await applyResize(buildExport());
  };

  const handleApplyToEditor = () => {
    const imageUrl = removeBg && cutoutUrl ? cutoutUrl : sourceImageUrl;
    if (!imageUrl || !hasActiveResizeSettings(selectedPreset)) return;
    onDone({
      preset: selectedPreset,
      resizeMode,
      removeBg,
      imageUrl,
      creationId: removeBg ? creationId : null,
    });
  };

  const handleAdjustResize = () => {
    setResizeApplyError(null);
    resetApplied();
  };

  const handleDone = async () => {
    setDoneBusy(true);
    try {
      if (resizeApplied && resizedPreviewUrl) {
        onDone({
          imageUrl: resizedPreviewUrl,
          removeBg,
          creationId: removeBg ? creationId : null,
          effectBaked: true,
        });
        return;
      }

      let imageUrl = sourceImageUrl;
      let doneCreationId = creationId;
      if (removeBg) {
        if (cutoutUrl) {
          imageUrl = cutoutUrl;
        } else {
          const removed = await runBackgroundRemoval();
          imageUrl = removed?.cutoutUrl;
          doneCreationId = removed?.creationId ?? doneCreationId;
        }
      }
      if (!imageUrl) {
        throw new Error("No image ready");
      }
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:")
      ) {
        throw new Error(
          "Background-removed image could not be loaded for the editor (CORS). Please try Remove Background again."
        );
      }
      onDone({
        preset: selectedPreset,
        resizeMode,
        removeBg,
        imageUrl,
        creationId: removeBg ? doneCreationId : null,
      });
    } catch (err) {
      setBgError(err?.message || "Could not finish resize setup");
    } finally {
      setDoneBusy(false);
    }
  };

  const controlsLocked =
    resizeApplied || resizeBusy || bgLoading || doneBusy || downloadBusy;

  const canApplyResize =
    Boolean(sourceImageUrl) &&
    hasActiveResizeSettings(selectedPreset) &&
    !resizeBusy;

  const configureFooterHint = !sourceImageUrl
    ? "Upload an image to start resizing."
    : !hasActiveResizeSettings(selectedPreset)
      ? "Choose a size preset or Custom, then click Apply Resize."
      : "Pick Fit or Fill, then click Apply Resize.";

  const title = selectedPreset?.name || "Resize";

  const selectedParsed = parsePresetPixelSize(selectedPreset);
  const customInitialWidth =
    isCustomResizePreset(selectedPreset) && selectedParsed
      ? selectedParsed.width
      : naturalSize.width || null;
  const customInitialHeight =
    isCustomResizePreset(selectedPreset) && selectedParsed
      ? selectedParsed.height
      : naturalSize.height || null;

  const leftContent = (
    <div className="rtp-panel">
      <div className="rtp-sticky-top">
        <ToolPrepSourceCards
          originalImageUrl={sourceImageUrl}
          previewImageUrl={removeBg && cutoutUrl ? cutoutUrl : null}
          removeBgActive={removeBg}
          cutoutPending={bgLoading && !cutoutUrl}
          onUploadClick={openUploadPicker}
          onRemove={clearSource}
          uploadProgress={uploadProgress}
          sourceName={sourceFileName}
          sourceSizeLabel={sourceFileSizeLabel}
          actionsDisabled={bgLoading || doneBusy || downloadBusy}
          removeBgControls={{
            active: removeBg,
            onToggle: handleRemoveBgToggle,
            disabled:
              removeBgRequired ||
              bgLoading ||
              !sourceImageUrl ||
              resizeApplied ||
              resizeBusy,
            locked: removeBgRequired || resizeApplied,
            bio: "Optional — remove the backdrop before resizing so Fit/Fill use the subject.",
            error: bgError,
          }}
        />

        {selectedPreset ? (
          <ResizeModeCards
            resizeMode={resizeMode}
            onModeChange={handleResizeModeChange}
            imageUrl={previewImageUrl}
            preset={selectedPreset}
          />
        ) : null}

        <ResizePresetSearch value={searchTerm} onChange={setSearchTerm} />
      </div>

      <div className="rtp-scroll-body tool-prep-scroll" ref={scrollBodyRef}>
        <ResizeControlMenu
          onResizeSelect={handlePresetSelect}
          onCustomPresetActivate={handleCustomPresetActivate}
          selectedPresetId={selectedPreset?.id ?? null}
          selectedPreset={selectedPreset}
          customFlyoutOpen={flyoutOpen}
          searchTerm={searchTerm}
          hideSearch
        />
      </div>
    </div>
  );

  return (
    <>
      <ImagePickerModal {...imagePickerProps} />
      <ToolPrepPopup
        toolKey="resize"
        title={title}
        left={leftContent}
        leftColumnRef={leftPanelRef}
        leftColumnClassName={flyoutOpen ? "has-blocks-overlay" : ""}
        preview={
          resizeApplied ? (
            <ToolPrepFlatResultStage
              className="resize-preview"
              resultImageUrl={resizedPreviewUrl}
              applying={resizeBusy}
              applyingLabel="Applying resize…"
              resultAlt="Resized result"
              softWatermarkToolKey="resize"
              emptyLabel={
                uploadProgress != null && !previewImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          ) : (
            <ResizePreview
              imageUrl={previewImageUrl}
              preset={selectedPreset}
              resizeMode={resizeMode}
              loading={bgLoading}
              applying={resizeBusy}
              applyingLabel="Applying resize…"
              showSubjectBox={Boolean(removeBg && cutoutUrl)}
              emptyLabel={
                uploadProgress != null && !previewImageUrl
                  ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                  : undefined
              }
            />
          )
        }
        onDone={
          openedFromEditor
            ? handleApplyToEditor
            : resizeApplied
              ? handleDone
              : handleApplyResize
        }
        onCancel={onCancel}
        confirmClose={Boolean(sourceImageUrl)}
        onDownload={
          openedFromEditor
            ? null
            : resizeApplied
              ? () => handleDownload(buildExport())
              : null
        }
        onOpenInEditor={
          openedFromEditor ? null : resizeApplied ? handleDone : null
        }
        onSecondaryAction={
          openedFromEditor ? null : resizeApplied ? handleAdjustResize : null
        }
        secondaryActionLabel="Adjust resize"
        busy={
          openedFromEditor
            ? bgLoading
            : resizeBusy || doneBusy || bgLoading || downloadBusy
        }
        busyLabel="Applying resize…"
        showDone={openedFromEditor || !resizeApplied}
        doneDisabled={!canApplyResize}
        doneLabel="Apply Resize"
        footerActionOrder={
          resizeApplied
            ? ["cancel", "secondary", "download", "openInEditor"]
            : ["cancel", "primary"]
        }
        openInEditorLabel="Open in Editor"
        footerHint={
          resizeApplyError ||
          (resizeApplied ? TOOL_PREP_RESULT_FOOTER_HINT : configureFooterHint)
        }
        footerHintClassName={
          resizeApplyError ? "tool-prep-popup__footer-hint--error" : ""
        }
      />
      <ResizeCustomSizeFlyout
        open={flyoutOpen}
        anchorEl={flyoutAnchor}
        panelRef={leftPanelRef}
        naturalWidth={naturalSize.width}
        naturalHeight={naturalSize.height}
        initialWidth={customInitialWidth}
        initialHeight={customInitialHeight}
        onContinue={handleCustomContinue}
        onClose={closeFlyout}
      />
      <ToolPrepDownloadFlowModals
        flow={downloadFlow}
        toolKey="resize"
        onPrimaryAction={() => {
          downloadFlow.handleThankYouDismiss();
          clearSource();
        }}
        onBackToDashboard={onCancel}
      />
    </>
  );
};

export default ResizeToolPrep;
